import type { SummarizeData } from '@brighterly/lib-core-types/ai-summarize/interfaces'

import { useAiSummarizeStore } from './store'
import { useAiSummaryApi } from '../api/ai-summary.hook'

interface AiSummaryLoadReturn {
  load: (bookingId: number) => Promise<void>
}

export const useAiSummaryLoad = (): AiSummaryLoadReturn => {
  const aiSummarizeStore = useAiSummarizeStore()

  const applySummary = (bookingId: number, data: SummarizeData): void => {
    aiSummarizeStore.setBookingId(bookingId)
    aiSummarizeStore.setSummaryId(data.id)
    aiSummarizeStore.setDuration(data.duration)
    aiSummarizeStore.setLessonDate(data.lesson_date)
    aiSummarizeStore.setSpeakingTime(data.speaking_time)
    aiSummarizeStore.setTopic(data.topic)
    aiSummarizeStore.setTitle(data.title)
    aiSummarizeStore.setRecordingUrl(data.recording_url)
    aiSummarizeStore.setSummary(data.summary)
    aiSummarizeStore.setHomeTasks(data.home_tasks || [])
    aiSummarizeStore.setHomeActivities(data.home_activities || [])
    aiSummarizeStore.setHighlights(data.highlights || [])

    if (!data.survey) return

    const answer = data.survey.answers[0]?.answer as number | null
    aiSummarizeStore.setSurvey(true)
    aiSummarizeStore.setRating(answer || 0)
    aiSummarizeStore.setComment(data.survey.answers[0]?.comment || null)
  }

  const load = async (bookingId: number): Promise<void> => {
    const requestId = aiSummarizeStore.begin(bookingId)
    const isSuperseded = (): boolean => requestId !== aiSummarizeStore.requestId

    await useAiSummaryApi({
      params: { summarize: String(bookingId) },
    }, {
      onSuccess: (data: SummarizeData) => {
        if (isSuperseded()) return

        applySummary(bookingId, data)
        aiSummarizeStore.settle(bookingId, requestId)
      },
      onError: () => aiSummarizeStore.fail(bookingId, requestId),
    })
  }

  return { load }
}
